'use client'

import React, { useMemo } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Chip from '@mui/material/Chip'
import Button from '@mui/material/Button'
import { Icon } from '@iconify/react'
import PremiumModuleTable from '../../../CreditNote/components/common/PremiumModuleTable'

const ExportListTable = ({
    data,
    loading,
    totalRecords,
    currentPage,
    itemsPerPage,
    onPageChange,
    onItemsPerPageChange
}) => {

    const downloadExcel = (relativePath) => {
        if (relativePath) {
            // Ensure double slashes are cleaned up
            const safeUrl = relativePath.replace(/([^:]\/)\/+/g, "$1").replace(/\\/g, "/");

            const link = document.createElement('a');
            link.href = safeUrl;
            link.download = safeUrl.split('/').pop();
            link.target = '_blank';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } else {
            alert("File not available.");
        }
    };

    const columns = useMemo(() => [
        {
            header: 'Export Name',
            accessorKey: 'ExportName',
            cell: ({ row }) => (
                <Typography variant='body2' sx={{ fontWeight: 500, color: 'primary.main' }}>
                    {row.original.ExportName || 'N/A'}
                </Typography>
            )
        },
        {
            header: 'Created Date',
            accessorKey: 'CreatedDate',
            cell: ({ row }) => (
                <Typography variant='body2' sx={{ color: 'text.secondary' }}>
                    {row.original.CreatedDate || row.original.CreatedOn || 'N/A'}
                </Typography>
            )
        },
        {
            header: 'Exported File',
            accessorKey: 'ExportedFileName',
            cell: ({ row }) => (
                <Typography variant='body2' sx={{ fontWeight: 400 }}>
                    {row.original.ExportedFileName || 'N/A'}
                </Typography>
            )
        },
        {
            header: 'Total Records',
            accessorKey: 'TotalRecordsExported',
            cell: ({ row }) => (
                <Typography variant='body2' sx={{ fontWeight: 600 }}>
                    {row.original.TotalRecordsExported || '0'}
                </Typography>
            )
        },
        {
            header: 'Status',
            accessorKey: 'Status',
            cell: ({ row }) => {
                const status = (row.original.Status || '').toLowerCase();
                let color = 'primary';
                if (status === 'processed' || status === 'completed') color = 'success';
                else if (status === 'failed') color = 'error';
                else if (status === 'unprocessed' || status === 'pending') color = 'warning';

                return (
                    <Chip
                        label={row.original.Status || 'Pending'}
                        color={color}
                        variant='tonal'
                        size='small'
                        sx={{ fontWeight: 500, textTransform: 'capitalize' }}
                    />
                )
            }
        },
        {
            header: 'Action',
            id: 'action',
            cell: ({ row }) => {
                const status = (row.original.Status || '').toLowerCase();
                const hasFile = !!row.original.ExportedFilePath;
                const isUnprocessed = status === 'unprocessed';

                if (isUnprocessed) return null;

                return (
                    <Button
                        variant="contained"
                        size="small"
                        color="success"
                        startIcon={<Icon icon="ri:file-download-line" />}
                        onClick={() => downloadExcel(row.original.ExportedFilePath)}
                        sx={{ textTransform: 'none', borderRadius: '4px' }}
                        disabled={!hasFile}
                    >
                        Download Excel
                    </Button>
                )
            }
        }
    ], []);

    return (
        <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <PremiumModuleTable
                data={data}
                columns={columns}
                totalRecords={totalRecords}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={onPageChange}
                onItemsPerPageChange={onItemsPerPageChange}
                loading={loading}
                hasFetched={true}
            />
        </Box>
    )
}

export default ExportListTable
